import { requestTextToSpeech } from '../api/client';
import {
    DEFAULT_READING_RATE,
    normalizeReadingRate,
    type InclusiveState,
    type VoiceEngine,
} from '../types';

const speechLangByCode: Record<string, string> = {
    en: 'en-US',
    eng: 'en-US',
    lug: 'lg-UG',
    swa: 'sw-KE',
    kin: 'rw-RW',
};

export type BrowserVoiceOption = {
    voiceURI: string;
    name: string;
    lang: string;
};

let currentAudio: HTMLAudioElement | null = null;
let currentUtterance: SpeechSynthesisUtterance | null = null;
let chunks: string[] = [];
let readingLanguage = 'en';
let preferredVoiceURI: string | null = null;
let currentReadingRate = DEFAULT_READING_RATE;
let active = false;
let playback = 0;
let ttsAbort: AbortController | null = null;
let cancelPlayback: (() => void) | null = null;
let readingListener: ((reading: boolean) => void) | null = null;
let readingStartedAt = 0;

const STOP_GRACE_MS = 500;

export function warmupSpeech(): void {
    window.speechSynthesis?.getVoices();
}

export function isReading(): boolean {
    return active;
}

export function listBrowserVoices(
    language?: string,
    selectedURI?: string | null,
): BrowserVoiceOption[] {
    if (!window.speechSynthesis) {
        return [];
    }

    const voices = window.speechSynthesis.getVoices();
    const targetLang = language
        ? (speechLangByCode[language] ?? `${language}-UG`)
        : null;
    const prefix = targetLang?.split('-')[0] ?? null;

    let filtered = voices;

    if (prefix) {
        const matching = voices.filter(
            (voice) =>
                voice.lang === targetLang || voice.lang.startsWith(prefix),
        );

        if (matching.length > 0) {
            filtered = matching;
        }
    }

    const ranked = [...filtered].sort(
        (left, right) => naturalnessScore(right) - naturalnessScore(left),
    );

    const options = ranked.map((voice) => ({
        voiceURI: voice.voiceURI,
        name: voice.name,
        lang: voice.lang,
    }));

    if (
        selectedURI &&
        !options.some((option) => option.voiceURI === selectedURI)
    ) {
        const selected = voices.find((voice) => voice.voiceURI === selectedURI);

        if (selected) {
            options.unshift({
                voiceURI: selected.voiceURI,
                name: selected.name,
                lang: selected.lang,
            });
        }
    }

    return options;
}

export async function readPage(
    state: InclusiveState,
    options?: { onChange?: (reading: boolean) => void },
): Promise<void> {
    if (options?.onChange) {
        readingListener = options.onChange;
    }

    if (isReading()) {
        if (Date.now() - readingStartedAt < STOP_GRACE_MS) {
            return;
        }

        stopReading();

        return;
    }

    const text = pageText();

    if (text === '') {
        return;
    }

    const token = ++playback;
    readingStartedAt = Date.now();
    active = true;
    currentReadingRate = normalizeReadingRate(state.readingRate);
    preferredVoiceURI = state.browserVoiceURI;
    queueMicrotask(() => {
        if (active && token === playback) {
            readingListener?.(true);
        }
    });

    const engine: VoiceEngine = state.voiceEngine ?? 'auto';

    if (engine === 'browser') {
        speakWithBrowser(text, state.language, preferredVoiceURI, token);

        return;
    }

    const cloudEngines = cloudEnginesFor(state, engine);

    for (const cloud of cloudEngines) {
        const played = await speakWithServer(state, text, token, cloud);

        if (token !== playback || !isReading()) {
            return;
        }

        if (played) {
            return;
        }
    }

    if (engine === 'auto') {
        speakWithBrowser(text, state.language, preferredVoiceURI, token);

        return;
    }

    notify(false);
}

function cloudEnginesFor(
    state: InclusiveState,
    engine: VoiceEngine,
): Array<'sunbird' | 'azure' | 'google'> {
    if (engine === 'sunbird' || engine === 'azure' || engine === 'google') {
        return [engine];
    }

    if (engine !== 'auto') {
        return [];
    }

    const engines: Array<'sunbird' | 'azure' | 'google'> = ['sunbird'];

    if (state.azureTtsAvailable) {
        engines.push('azure');
    }

    if (state.googleTtsAvailable) {
        engines.push('google');
    }

    return engines;
}

export function stopReading(): void {
    playback += 1;
    readingStartedAt = 0;
    ttsAbort?.abort();
    ttsAbort = null;
    cancelPlayback?.();
    cancelPlayback = null;
    currentAudio?.pause();
    currentAudio = null;
    stopBrowserSpeech();
    notify(false);
}

function finishReading(): void {
    currentAudio = null;
    currentUtterance = null;
    chunks = [];
    notify(false);
}

function notify(reading: boolean): void {
    active = reading;
    readingListener?.(reading);
}

function pageText(): string {
    const main = document.querySelector<HTMLElement>(
        'main, [role="main"], article, #content, .content',
    );
    const root = main ?? document.body;
    const clone = root.cloneNode(true) as HTMLElement;
    clone
        .querySelectorAll(
            [
                '#inclusive-widget',
                '#inclusive-skip-link',
                '#inclusive-access-link',
                '[hidden]',
                '[aria-hidden="true"]',
                'script',
                'style',
                'noscript',
                'template',
                'svg',
                'nav',
                'header',
                'footer',
                'aside',
                '[role="navigation"]',
                '[role="banner"]',
                '[role="contentinfo"]',
                '[role="complementary"]',
            ].join(', '),
        )
        .forEach((node) => node.remove());
    const text = (clone.innerText || clone.textContent || '')
        .replace(/\s+/g, ' ')
        .trim();

    return text.slice(0, 1600);
}

async function speakWithServer(
    state: InclusiveState,
    text: string,
    token: number,
    engine: 'sunbird' | 'azure' | 'google',
): Promise<boolean> {
    const parts = splitForSpeech(text, 420);
    let started = false;
    const abort = new AbortController();
    ttsAbort = abort;
    const allowBrowserFallback = state.voiceEngine === 'auto';

    try {
        for (let index = 0; index < parts.length; index++) {
            if (token !== playback || !isReading() || abort.signal.aborted) {
                return started;
            }

            const part = parts[index];

            if (part === undefined || part === '') {
                continue;
            }

            const audioUrl = await requestTextToSpeech(
                state.options,
                part,
                state.language,
                abort.signal,
                engine,
                currentReadingRate,
            );

            if (
                token !== playback ||
                abort.signal.aborted ||
                ttsAbort !== abort
            ) {
                return started;
            }

            if (audioUrl === null) {
                if (!started) {
                    return false;
                }

                if (allowBrowserFallback) {
                    speakWithBrowser(
                        parts.slice(index).join(' '),
                        state.language,
                        preferredVoiceURI,
                        token,
                    );
                } else {
                    notify(false);
                }

                return true;
            }

            const played = await playAudioUrl(audioUrl, token, engine);

            if (!played) {
                if (!started) {
                    return false;
                }

                if (allowBrowserFallback) {
                    speakWithBrowser(
                        parts.slice(index).join(' '),
                        state.language,
                        preferredVoiceURI,
                        token,
                    );
                } else {
                    notify(false);
                }

                return true;
            }

            started = true;
        }
    } finally {
        if (ttsAbort === abort) {
            ttsAbort = null;
        }
    }

    if (started && token === playback && isReading()) {
        finishReading();
    }

    return started;
}

function playAudioUrl(
    url: string,
    token: number,
    engine: 'sunbird' | 'azure' | 'google',
): Promise<boolean> {
    return new Promise((resolve) => {
        if (token !== playback || !isReading()) {
            resolve(false);

            return;
        }

        const audio = new Audio(url);
        audio.loop = false;
        // Azure/Google already synthesize at speakingRate; only slow Sunbird client-side.
        audio.playbackRate = engine === 'sunbird' ? currentReadingRate : 1;
        if ('preservesPitch' in audio) {
            audio.preservesPitch = true;
        }
        currentAudio = audio;
        let settled = false;

        const finish = (ok: boolean): void => {
            if (settled) {
                return;
            }

            settled = true;
            cancelPlayback = null;

            if (currentAudio === audio) {
                currentAudio = null;
            }

            resolve(ok && token === playback && isReading());
        };

        cancelPlayback = () => {
            audio.pause();
            finish(false);
        };

        audio.addEventListener('ended', () => finish(true), { once: true });
        audio.addEventListener('error', () => finish(false), { once: true });

        void audio.play().then(
            () => undefined,
            () => finish(false),
        );
    });
}

function speakWithBrowser(
    text: string,
    language: string,
    voiceURI: string | null,
    token: number = playback,
): void {
    if (!window.speechSynthesis || token !== playback || !isReading()) {
        if (token === playback) {
            notify(false);
        }

        return;
    }

    // Clear any prior queue once, then speak chunks without cancel/resume
    // between them — cancel()+resume() makes Chromium re-speak the same text.
    window.speechSynthesis.cancel();
    currentUtterance = null;
    readingLanguage = language;
    preferredVoiceURI = voiceURI;
    chunks = splitForSpeech(text, 260);
    speakNextChunk(token);
}

function speakNextChunk(token: number): void {
    if (token !== playback || !isReading() || !window.speechSynthesis) {
        return;
    }

    const next = chunks.shift();

    if (!next) {
        currentUtterance = null;

        if (currentAudio === null) {
            notify(false);
        }

        return;
    }

    const utterance = new SpeechSynthesisUtterance(next);
    utterance.lang =
        speechLangByCode[readingLanguage] ?? `${readingLanguage}-UG`;
    utterance.rate = currentReadingRate;
    utterance.pitch = 1;
    const voice = pickVoice(utterance.lang, preferredVoiceURI);

    if (voice) {
        utterance.voice = voice;
    }

    utterance.onend = () => {
        if (currentUtterance !== utterance || token !== playback) {
            return;
        }

        currentUtterance = null;
        queueMicrotask(() => speakNextChunk(token));
    };
    utterance.onerror = (event) => {
        if (currentUtterance !== utterance || token !== playback) {
            return;
        }

        if (event.error === 'canceled' || event.error === 'interrupted') {
            return;
        }

        finishReading();
    };

    currentUtterance = utterance;
    window.speechSynthesis.speak(utterance);
}

function stopBrowserSpeech(): void {
    currentUtterance = null;
    chunks = [];
    window.speechSynthesis?.cancel();
}

function splitForSpeech(text: string, maxLength: number): string[] {
    const parts = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [text];
    const grouped: string[] = [];
    let current = '';

    for (const part of parts) {
        if (current.length + part.length > maxLength && current !== '') {
            grouped.push(current.trim());
            current = part;
        } else {
            current += part;
        }
    }

    if (current.trim() !== '') {
        grouped.push(current.trim());
    }

    return grouped;
}

function pickVoice(
    lang: string,
    preferredURI: string | null,
): SpeechSynthesisVoice | undefined {
    const voices = window.speechSynthesis.getVoices();

    if (preferredURI) {
        const preferred = voices.find((voice) => voice.voiceURI === preferredURI);

        if (preferred) {
            return preferred;
        }
    }

    const prefix = lang.split('-')[0] ?? lang;
    const matching = voices.filter(
        (voice) => voice.lang === lang || voice.lang.startsWith(prefix),
    );

    if (matching.length === 0) {
        return voices[0];
    }

    return [...matching].sort(
        (left, right) => naturalnessScore(right) - naturalnessScore(left),
    )[0];
}

function naturalnessScore(voice: SpeechSynthesisVoice): number {
    const label = `${voice.name} ${voice.voiceURI}`.toLowerCase();
    let score = 0;

    if (
        /neural|natural|premium|enhanced|wavenet|studio|chirp|online|google|microsoft|samantha|aria|jenny|guy|nova|shimmer/.test(
            label,
        )
    ) {
        score += 4;
    }

    if (!voice.localService) {
        score += 2;
    }

    if (/compact|eloquence|espeak|robot|dumb/.test(label)) {
        score -= 3;
    }

    return score;
}
